/**
 * PATH: packages/email-hub/src/feature/components/OutlookBanner.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-OUTLOOKBANNER-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/OutlookBanner.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/OutlookBanner.tsx:
 *     thin re-export shim that points back here.
 */

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  RefreshCw,
  WifiOff,
} from 'lucide-react'
import type { OutlookStatus } from '../hooks/useEmails'
import { canSendEmail } from '../lib/emailCapability'

type Variant = 'unreachable' | 'expired' | 'disconnected' | 'expiringSoon' | 'connected' | 'unknown'

type Props = {
  status: OutlookStatus & { expiringSoon?: boolean }
  onConnect: () => void | Promise<void>
  onDismiss?: () => void
  /** When true, renders nothing for the healthy/unknown state. Useful when
   *  the consumer only wants to surface the banner on problems. */
  compact?: boolean
  /** When true, the user has at least one shared-mailbox grant in
   *  /api/email-accounts/, so they can send/sync regardless of their
   *  personal OAuth state. Suppresses the nag.
   *  See BUG-2026-05-13-OUTLOOK-BANNER-SHARED-MAILBOX (Mark Newman) and
   *  the `canSendEmail` helper in lib/emailCapability.ts. */
  hasSharedMailboxAccess?: boolean
  className?: string
}

function resolveVariant(
  status: Props['status'],
  hasSharedMailboxAccess: boolean,
): Variant {
  // Three independent BE signals can each say "this user can send/sync":
  //   1. `status.connected`          — user has personal OutlookToken.
  //   2. `status.orgConnected`       — any org user has OutlookToken.
  //   3. `hasSharedMailboxAccess`    — user has a grant in /api/email-accounts/
  //                                    (modern multi-mailbox / shared-inbox
  //                                    table — sidebar reads this).
  //
  // The pre-fix resolver only consulted (1); qf!2902 added (2); this revision
  // adds (3) via the `canSendEmail` helper. Every nag-banner consumer must
  // route through THAT helper from now on — see
  // ROBUSTNESS_PRINCIPLES.md ::
  //   `if-a-piece-of-state-gates-ui-EVERY-consumer-must-gate-on-the-same-rule`
  //
  // BUG trail: qb!2796 / qf!2795 (shell banner) → qf!2902
  // (per-page banner + EmailConnectionBanner) → this MR
  // (shared-mailbox source).
  if (status.reachable === false) return 'unreachable'
  if (status.connected === null) return 'unknown'

  if (canSendEmail({ outlook: status, sharedMailboxes: hasSharedMailboxAccess ? [{}] : [] })) {
    return 'connected'
  }
  if (status.connected === false) return 'disconnected'
  if (status.isExpired) return 'expired'
  if (status.expiringSoon) return 'expiringSoon'
  return 'connected'
}

// BUG-0011 (extended 2026-05-13): the original tones used dark-mode
// colour tokens only (text-amber-200, text-red-300, text-slate-300,
// text-emerald-300). Rendered on a light theme they produce light
// text on a near-white background — completely unreadable, exactly
// what the operator screenshotted: the banner title was a faint
// yellow shadow and the body copy disappeared into a grey strip.
// EmailConnectionBanner was already migrated to light/dark-aware
// tokens; this brings OutlookBanner in line so any consumer (mixed-
// state path in EmailConnectionBanner, SyncSettings, etc.) renders
// legibly in BOTH themes without a fork.
const TONES: Record<Variant, string> = {
  unreachable: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100',
  expired: 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-100',
  disconnected: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100',
  expiringSoon: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100',
  connected: 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-100',
  unknown: 'border-border bg-muted/20 text-muted-foreground',
}

export default function OutlookBanner({
  status,
  onConnect,
  onDismiss,
  compact,
  hasSharedMailboxAccess,
  className,
}: Props) {
  const variant = resolveVariant(status, hasSharedMailboxAccess === true)

  if (compact && (variant === 'connected' || variant === 'unknown')) return null

  const Icon = (
    variant === 'connected' ? CheckCircle2
    : variant === 'expiringSoon' ? Clock
    : variant === 'unreachable' ? WifiOff
    : AlertCircle
  )

  const title = (
    variant === 'unreachable' ? 'Outlook status unavailable'
    : variant === 'expired' ? 'Microsoft session expired'
    : variant === 'disconnected' ? 'Microsoft email not connected'
    : variant === 'expiringSoon' ? 'Microsoft session expires soon'
    : variant === 'connected' ? `Connected to ${status.email ?? 'Microsoft'}`
    : 'Checking Outlook…'
  )

  const body = (
    variant === 'unreachable' ? 'Couldn\'t reach the Outlook status service. Sending will fall back to SMTP if available.'
    : variant === 'expired' ? 'Your Microsoft sign-in has expired. Reconnect to send and sync email.'
    : variant === 'disconnected' ? 'Connect your Microsoft account to send and sync email directly from your mailbox. Sending will use SMTP fallback until you do.'
    : variant === 'expiringSoon' ? 'Your Microsoft session expires in less than 5 minutes. Reconnect now to avoid mid-send failures.'
    : variant === 'connected' ? 'Emails you send from here go out through your Microsoft mailbox.'
    : null
  )

  const actionLabel = (
    variant === 'expired' || variant === 'expiringSoon' ? 'Reconnect'
    : variant === 'disconnected' ? 'Connect Microsoft'
    : variant === 'unreachable' ? 'Retry'
    : null
  )

  return (
    <div
      className={`flex items-start gap-3 p-3 border rounded-lg ${TONES[variant]} ${className ?? ''}`}
      role={variant === 'expired' || variant === 'disconnected' ? 'alert' : 'status'}
      aria-live={variant === 'expired' ? 'assertive' : 'polite'}
    >
      <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {body && <div className="text-xs opacity-90 mt-0.5">{body}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {actionLabel && (
          <button
            type="button"
            onClick={() => { void onConnect() }}
            className="text-xs px-2.5 py-1 rounded-md bg-[#0078D4] text-white hover:bg-[#006CBE] transition-colors flex items-center gap-1.5"
          >
            {variant === 'unreachable' ? <RefreshCw className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
            {actionLabel}
          </button>
        )}
        {onDismiss && variant === 'connected' && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs opacity-70 hover:opacity-100"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  )
}
